import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Alert } from '../../components';
import SocialSettingsForm from './SocialSettingsForm';
import WorkflowList from './WorkflowList';
import DeleteSocialPostButton from './DeleteSocialPostButton';
import { parse } from '../../util/urlHelpers';
import { reviewPostHref } from '../AdminSocialReviewerPage/fullscreenHelpers';
import { TRIGGER_LABELS } from './WorkflowEditor/triggerLabels';
import { PLATFORM_LABELS } from './platformLabels';
import { postStatusLabelForPost, postStatusTone, deleteBlockedReason } from './socialStatusLabels';
import {
    dashboardEmptyState,
    pendingTabCounts,
    pendingTabCaption,
    hasInFlightPost,
    refreshAgoLabel,
    REFRESH_POLL_MS,
} from './dashboardHelpers';
import {
    DEFAULT_SOCIAL_POSTS_TAB,
    SOCIAL_POST_TAB_STATUSES,
    isValidSocialPostsRouteTab,
    queryPosts,
    fetchPendingCount,
    fetchTriggers,
    fetchWorkflows,
    fetchSettings,
    deletePost,
} from './AdminSocialPostsPage.duck';
import css from './AdminSocialPostsPage.css';

// The German label for a post's workflow (trigger): the server registry wins, then
// the client fallback map, then the raw key. Pure — reused by the card chip.
export const triggerLabelFor = (triggers, trigger) => {
    if (!trigger) return '';
    const found = (triggers || []).find(t => t.key === trigger);
    return (found && found.label) || TRIGGER_LABELS[trigger] || trigger;
};

const TABS = [
    { key: 'pending', label: 'Ausstehend', statuses: SOCIAL_POST_TAB_STATUSES.pending },
    { key: 'published', label: 'Veröffentlicht', statuses: SOCIAL_POST_TAB_STATUSES.published },
    { key: 'failed', label: 'Fehlgeschlagen', statuses: SOCIAL_POST_TAB_STATUSES.failed },
    { key: 'all', label: 'Alle', statuses: SOCIAL_POST_TAB_STATUSES.all },
    { key: 'workflow', label: 'Workflow', statuses: null },
    { key: 'settings', label: 'Einstellungen', statuses: null },
];

const validTab = key => TABS.some(tab => tab.key === key);
const tabFromSearch = search => {
    const { tab } = parse(search || '');
    if (!isValidSocialPostsRouteTab(tab)) return DEFAULT_SOCIAL_POSTS_TAB;
    return tab && validTab(tab) ? tab : DEFAULT_SOCIAL_POSTS_TAB;
};

// The shared vocabulary answers WHAT a status means; this maps that answer onto
// the dashboard's own pill tokens (the reviewer maps the same tones differently).
const TONE_CLASS = {
    ok: css.statusPublished,
    error: css.statusFailed,
    warn: css.statusPending,
    neutral: css.statusNeutral,
    info: css.statusInfo,
};
const statusClass = status => TONE_CLASS[postStatusTone(status)] || css.statusInfo;

// First-load placeholder — three card-shaped rows, so the list does not jump when
// the real cards arrive. Only ever shown when there is nothing to show yet: a poll
// refreshing an existing list must never blink it away.
const ListSkeleton = () => (
    <div className={css.cardList} role="status" aria-label="Beiträge werden geladen">
        {[0, 1, 2].map(i => (
            <div key={i} className={`${css.card} ${css.cardSkeleton}`} aria-hidden="true">
                <span className={`${css.skel} ${css.skelThumb}`} />
                <span className={css.skelCardBody}>
                    <span className={`${css.skel} ${css.skelTitle}`} />
                    <span className={`${css.skel} ${css.skelLine}`} />
                </span>
                <span className={`${css.skel} ${css.skelPill}`} />
            </div>
        ))}
    </div>
);

export const SocialDashboardComponent = props => {
    const {
        history,
        posts,
        triggers,
        workflows,
        settings,
        queryInProgress,
        queryError,
        actionError,
        pendingCount,
        deletingPostIds,
        onQueryPosts,
        onFetchPendingCount,
        onFetchTriggers,
        onFetchWorkflows,
        onFetchSettings,
        onDeletePost,
    } = props;

    const search = (history && history.location && history.location.search) || '';
    const requestedTab = tabFromSearch(search);
    const requestedWorkflow = parse(search || '').workflow || '';

    const [activeTab, setActiveTab] = useState(requestedTab);
    const [workflowFilter, setWorkflowFilter] = useState(requestedWorkflow);

    useEffect(() => {
        setActiveTab(requestedTab);
    }, [requestedTab]);

    useEffect(() => {
        setWorkflowFilter(requestedWorkflow);
    }, [requestedWorkflow]);

    // The filter options are the workflows (label = name, value = slug); triggers
    // still feed the legacy card-chip fallback. Settings tell an empty list apart
    // from a switched-off automation.
    useEffect(() => {
        onFetchTriggers();
        onFetchWorkflows();
        onFetchSettings();
    }, [onFetchTriggers, onFetchWorkflows, onFetchSettings]);

    // The ONE way this page loads its list — the tab/filter effect, the 25s poll
    // and the manual refresh button all go through it.
    const refreshList = useCallback(() => {
        const tab = TABS.find(t => t.key === activeTab);
        if (!tab || tab.statuses === null) return;
        onQueryPosts(tab.statuses, { workflow: workflowFilter || undefined });
        onFetchPendingCount();
    }, [activeTab, workflowFilter, onQueryPosts, onFetchPendingCount]);

    // Query the list whenever the active tab OR the workflow filter changes (list
    // tabs only). The filter composes with the tab's statuses server-side.
    useEffect(() => {
        refreshList();
    }, [refreshList]);

    // ---- Freshness (F4) ----
    // A post being generated or sent changes WITHOUT anyone touching this page, so
    // the list refreshes itself while any such row is on screen — and only then.
    // A settled list polls nothing.
    const [loadedAt, setLoadedAt] = useState(null);
    const [clock, setClock] = useState(() => Date.now());
    const wasQueryingRef = useRef(false);
    useEffect(() => {
        if (wasQueryingRef.current && !queryInProgress && !queryError) {
            setLoadedAt(Date.now());
            setClock(Date.now());
        }
        wasQueryingRef.current = queryInProgress;
    }, [queryInProgress, queryError]);

    const listInFlight = hasInFlightPost(posts);
    const listTabActive = !['settings', 'workflow'].includes(activeTab);

    // Timer/visibility refreshes must never stack onto a still-running request
    // (a slow response would otherwise queue a second pair every tick, and an
    // out-of-order response could paint older truth). A tab/filter change still
    // refreshes unconditionally — that request is FOR the new view.
    const queryInProgressRef = useRef(queryInProgress);
    queryInProgressRef.current = queryInProgress;
    const refreshIfIdle = useCallback(() => {
        if (!queryInProgressRef.current) refreshList();
    }, [refreshList]);

    useEffect(() => {
        if (!listTabActive || !listInFlight || typeof document === 'undefined') return undefined;
        // A hidden tab polls nothing (a backgrounded dashboard must not keep the
        // API busy); coming back into view refreshes immediately instead of
        // waiting out the interval.
        const poll = setInterval(() => {
            if (!document.hidden) refreshIfIdle();
        }, REFRESH_POLL_MS);
        const onVisibility = () => {
            if (!document.hidden) refreshIfIdle();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearInterval(poll);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [listTabActive, listInFlight, refreshIfIdle]);

    // Keeps "Aktualisiert vor …" true while nothing else re-renders the page.
    useEffect(() => {
        if (!listTabActive || loadedAt === null || typeof document === 'undefined') {
            return undefined;
        }
        const tick = setInterval(() => {
            if (!document.hidden) setClock(Date.now());
        }, 5000);
        return () => clearInterval(tick);
    }, [listTabActive, loadedAt]);

    // Opening a post now hands off to the full-screen reviewer (the inline
    // ConfirmationPanel was superseded + removed in P4). The reviewer reads
    // ?post=ID and drives the per-platform review + publish.
    const openPost = useCallback(
        id => {
            history.push(reviewPostHref(id));
        },
        [history]
    );

    const pushList = (tab, workflow) => {
        if (!history || typeof history.push !== 'function') return;
        const params = new URLSearchParams();
        params.set('tab', tab);
        if (workflow) params.set('workflow', workflow);
        history.push(`/admin-social?${params.toString()}`);
    };

    const selectTab = key => {
        setActiveTab(key);
        // Preserve the workflow filter across tab switches (list tabs only).
        pushList(key, TABS.find(t => t.key === key && t.statuses !== null) ? workflowFilter : '');
    };

    const selectWorkflowFilter = value => {
        setWorkflowFilter(value);
        pushList(activeTab, value);
    };

    // The post's display title: listing title, or the rider's name for rider
    // triggers — the SAME chain the server's approval email uses (entityTitle in
    // SocialAutomationService). Only then the id-based fallback.
    const postTitle = post => {
        const ctx = post.context || {};
        return ctx.title || ctx.riderName || ctx.listingTitle || `Beitrag ${post.entityId}`;
    };

    const renderCard = post => {
        const ctx = post.context || {};
        const thumb = (ctx.imageUrls && ctx.imageUrls[0]) || null;
        const platforms = (post.channels || [])
            .map(c => PLATFORM_LABELS[c.platform] || c.platform)
            .join(' · ');
        const deleting = deletingPostIds.some(id => String(id) === String(post.id));
        // ONE answer drives both halves: a blocked delete is disabled AND says why.
        const blockedReason = deleteBlockedReason(post.status);
        const deleteDisabled = deleting || Boolean(blockedReason);
        return (
            <div key={post.id} className={css.card}>
                <button
                    type="button"
                    className={css.cardOpen}
                    onClick={() => openPost(post.id)}
                    disabled={deleting}
                >
                    {thumb ? (
                        <img className={css.thumb} src={thumb} alt="" />
                    ) : (
                        <div className={`${css.thumb} ${css.thumbEmpty}`}>
                            <span role="img" aria-label="Pferd">
                                🐴
                            </span>
                        </div>
                    )}
                    <div className={css.cardBody}>
                        <p className={css.cardTitle}>{postTitle(post)}</p>
                        <p className={css.cardMeta}>
                            {(post.workflowName || post.trigger) && (
                                <>
                                    <span className={css.triggerChip}>
                                        {post.workflowName ||
                                            triggerLabelFor(triggers, post.trigger)}
                                    </span>
                                    <span className={css.cardDot}>·</span>
                                </>
                            )}
                            {platforms || '—'}
                            <span className={css.cardDot}>·</span>
                            {new Date(post.createdAt).toLocaleString('de-CH')}
                        </p>
                    </div>
                    <span className={`${css.statusPill} ${statusClass(post.status)}`}>
                        {postStatusLabelForPost(post)}
                    </span>
                </button>
                <DeleteSocialPostButton
                    postId={post.id}
                    onDelete={onDeletePost}
                    disabled={deleteDisabled}
                    disabledReason={blockedReason}
                    className={css.cardDeleteBtn}
                />
            </div>
        );
    };

    const showSettings = activeTab === 'settings';
    const showWorkflow = activeTab === 'workflow';
    const showList = listTabActive;
    // The skeleton stands in for a list that is not there yet; a poll re-querying
    // an existing list leaves it on screen.
    const showSkeleton = queryInProgress && posts.length === 0;
    const emptyState = dashboardEmptyState({ settings, workflows, workflowFilter });
    const pendingCaption =
        activeTab === 'pending' ? pendingTabCaption(pendingTabCounts(posts)) : null;

    return (
        <div className={css.root}>
            <header className={css.pageHeader}>
                <h1 className={css.pageTitle}>Social Media Automation</h1>
                <p className={css.pageSubtitle}>
                    Entwürfe prüfen, freigeben und die Automation steuern.
                </p>
            </header>

            <nav className={css.tabs}>
                {TABS.map(tab => (
                    <button
                        type="button"
                        key={tab.key}
                        className={`${css.tab} ${activeTab === tab.key ? css.tabActive : ''}`}
                        onClick={() => selectTab(tab.key)}
                    >
                        {tab.label}
                        {/* The badge counts the REVIEWER QUEUE — the posts you can
                            actually approve — which is a subset of what this tab
                            lists (it also shows posts still being generated and
                            Community work). One meaning, stated in the label:
                            "zu prüfen". The caption below splits the same list. */}
                        {tab.key === 'pending' && pendingCount > 0 && (
                            <span
                                className={css.badge}
                                title={`${pendingCount} Beiträge warten auf deine Freigabe`}
                            >
                                {pendingCount}
                                <span className={css.badgeUnit}> zu prüfen</span>
                            </span>
                        )}
                    </button>
                ))}
            </nav>

            {queryError && showList && (
                <Alert
                    showAsPlainText
                    type="failure"
                    description={queryError}
                    closeAllowed={false}
                />
            )}
            {actionError && showList && (
                <Alert
                    showAsPlainText
                    type="failure"
                    description={actionError}
                    closeAllowed={false}
                />
            )}

            {showSettings && <SocialSettingsForm />}

            {showWorkflow && <WorkflowList history={history} />}

            {showList && (
                <div className={css.listFilterRow}>
                    <label className={css.filterLabel} htmlFor="wf-filter">
                        Workflow
                    </label>
                    <select
                        id="wf-filter"
                        className={css.filterSelect}
                        value={workflowFilter}
                        onChange={e => selectWorkflowFilter(e.target.value)}
                    >
                        <option value="">Alle Workflows</option>
                        {(workflows || []).map(w => (
                            <option key={w.slug} value={w.slug}>
                                {w.name || 'Standard'}
                            </option>
                        ))}
                    </select>

                    <span className={css.listFreshness}>
                        {pendingCaption ? (
                            <span className={css.listCaption}>{pendingCaption}</span>
                        ) : null}
                        {loadedAt !== null ? (
                            <span className={css.refreshAgo} aria-live="polite">
                                {refreshAgoLabel(clock - loadedAt)}
                            </span>
                        ) : null}
                        <button
                            type="button"
                            className={css.refreshBtn}
                            onClick={refreshList}
                            disabled={queryInProgress}
                        >
                            {queryInProgress ? 'Wird geladen…' : 'Aktualisieren'}
                        </button>
                    </span>
                </div>
            )}

            {showList &&
                (showSkeleton ? (
                    <ListSkeleton />
                ) : queryError ? null : posts.length === 0 ? (
                    <div className={css.emptyState}>
                        <Alert
                            showAsPlainText
                            type="info"
                            description={emptyState.message}
                            closeAllowed={false}
                        />
                        {emptyState.hint ? (
                            <p className={css.emptyStateHint}>{emptyState.hint}</p>
                        ) : null}
                        {emptyState.action ? (
                            <button
                                type="button"
                                className={css.emptyStateAction}
                                onClick={() => selectTab(emptyState.action.tab)}
                            >
                                {emptyState.action.label} →
                            </button>
                        ) : null}
                    </div>
                ) : (
                    <div className={css.cardList}>{posts.map(renderCard)}</div>
                ))}
        </div>
    );
};

const mapStateToProps = state => {
    const {
        posts,
        triggers,
        workflows,
        settings,
        queryInProgress,
        queryError,
        actionError,
        pendingCount,
        deletingPostIds,
    } = state.AdminSocialPostsPage;
    return {
        posts,
        triggers,
        workflows,
        settings,
        queryInProgress,
        queryError,
        actionError,
        pendingCount,
        deletingPostIds,
    };
};

const mapDispatchToProps = dispatch => ({
    onQueryPosts: (statuses, opts) => dispatch(queryPosts(statuses, opts)),
    onFetchPendingCount: () => dispatch(fetchPendingCount()),
    onFetchTriggers: () => dispatch(fetchTriggers()),
    onFetchWorkflows: () => dispatch(fetchWorkflows()),
    onFetchSettings: () => dispatch(fetchSettings()),
    onDeletePost: id => dispatch(deletePost(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SocialDashboardComponent);
